import { z } from 'zod';
import { LINE_ITEM_CATEGORIES } from '../types/breakdown';

const isoDateTime = () => z.string().datetime({ offset: true });

// Breakdown slot key
export const breakdownSlotKeySchema = z.enum(['generic', 'carpet']);

// Sub-item type - now a free-form string (validated against sub_item_categories at API layer)
export const subItemTypeSchema = z.string().min(1);

// Line item category (using as reference, but allowing any string for flexibility)
export const lineItemCategorySchema = z.enum(LINE_ITEM_CATEGORIES);

// Sub-item schemas
export const breakdownSubItemSchema = z.object({
  id: z.string().uuid(),
  lineItemId: z.string().uuid(),
  type: subItemTypeSchema,
  hours: z.number().nullable().optional(),
  rate: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  note: z.string().nullable().optional(),
  markupRate: z.number().nullable().optional(),
  sortOrder: z.number().int(),
  createdAt: isoDateTime(),
  updatedAt: isoDateTime(),
});

export const breakdownSubItemInputSchema = z.object({
  type: subItemTypeSchema,
  hours: z.number().optional(),
  rate: z.number().optional(),
  amount: z.number().optional(),
  note: z.string().optional(),
  markupRate: z.number().min(0).max(1).optional(),
  sortOrder: z.number().int().optional(),
});

export const breakdownSubItemUpdateSchema = z.object({
  type: subItemTypeSchema.optional(),
  hours: z.number().nullable().optional(),
  rate: z.number().nullable().optional(),
  amount: z.number().nullable().optional(),
  note: z.string().nullable().optional(),
  markupRate: z.number().min(0).max(1).nullable().optional(),
  sortOrder: z.number().int().optional(),
});

// Line item schemas
export const breakdownLineItemSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  category: z.string().min(1),
  description: z.string().nullable().optional(),
  sortOrder: z.number().int(),
  totalCost: z.number(),
  templateId: z.string().nullable().optional(),
  itemDate: z.string().nullable().optional(),
  // Whether the line shows sub-items on the invoice PDF. Optional w/ default so
  // existing consumers that build line items without it stay valid.
  itemized: z.boolean().optional().default(true),
  createdAt: isoDateTime(),
  updatedAt: isoDateTime(),
});

export const breakdownLineItemWithSubItemsSchema = breakdownLineItemSchema.extend({
  subItems: z.array(breakdownSubItemSchema),
});

export const breakdownLineItemInputSchema = z.object({
  category: z.string().min(1),
  description: z.string().optional(),
  sortOrder: z.number().int().optional(),
  templateId: z.string().optional(),
  itemDate: z.string().optional(),
});

export const breakdownLineItemUpdateSchema = z.object({
  category: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  sortOrder: z.number().int().optional(),
  templateId: z.string().nullable().optional(),
  itemDate: z.string().nullable().optional(),
});

// Snapshot schemas
export const snapshotProjectSchema = z.object({
  name: z.string(),
  sortOrder: z.number(),
  lineItems: z.array(z.object({
    category: z.string(),
    description: z.string().nullable().optional(),
    sortOrder: z.number(),
    templateId: z.string().nullable().optional(),
    totalCost: z.number(),
    itemDate: z.string().nullable().optional(),
    subItems: z.array(z.object({
      type: z.string(),
      hours: z.number().nullable().optional(),
      rate: z.number().nullable().optional(),
      rateType: z.string().nullable().optional(),
      amount: z.number().nullable().optional(),
      note: z.string().nullable().optional(),
      markupRate: z.number().nullable().optional(),
      sortOrder: z.number(),
    })),
  })),
  id: z.string().uuid().optional(),
});

export const breakdownSnapshotSchema = z.object({
  lineItems: z.array(z.object({
    category: z.string(),
    description: z.string().nullable().optional(),
    sortOrder: z.number(),
    templateId: z.string().nullable().optional(),
    totalCost: z.number(),
    itemDate: z.string().nullable().optional(),
    subItems: z.array(z.object({
      type: z.string(),
      hours: z.number().nullable().optional(),
      rate: z.number().nullable().optional(),
      rateType: z.string().nullable().optional(),
      amount: z.number().nullable().optional(),
      note: z.string().nullable().optional(),
      markupRate: z.number().nullable().optional(),
      sortOrder: z.number(),
    })),
  })),
  projects: z.array(snapshotProjectSchema),
  totalCost: z.number(),
  taxRate: z.union([z.string(), z.number()]).nullable().optional(),
  markupRate: z.union([z.string(), z.number()]).nullable().optional(),
  roundingPrecision: z.union([z.string(), z.number()]).nullable().optional(),
  expenseProjectLinks: z.array(z.object({
    expenseId: z.string().uuid(),
    projectId: z.string().uuid(),
  })).optional(),
});

// Bulk save schema - for saving entire breakdown at once
// Note: category can be empty for work-in-progress items
export const bulkBreakdownInputSchema = z.object({
  lineItems: z.array(z.object({
    id: z.string().uuid().optional(),
    category: z.string(),
    description: z.string().optional(),
    sortOrder: z.number().int(),
    templateId: z.string().optional(),
    itemDate: z.string().optional(),
    subItems: z.array(z.object({
      id: z.string().uuid().optional(),
      type: subItemTypeSchema,
      hours: z.number().optional(),
      rate: z.number().optional(),
      unitKey: z.string().optional(),
      amount: z.number().optional(),
      note: z.string().optional(),
      markupRate: z.number().min(0).max(1).optional(),
      sortOrder: z.number().int(),
    })),
  })),
});

// Full breakdown save schema - frontend sends entire state, backend diffs
export const saveBreakdownInputSchema = z.object({
  lineItems: z.array(z.object({
    id: z.string().uuid(),
    category: z.string(),
    description: z.string().optional(),
    sortOrder: z.number().int(),
    templateId: z.string().nullable().optional(),
    itemDate: z.string().nullable().optional(),
    projectId: z.string().uuid().nullable().optional(),
    subItems: z.array(z.object({
      id: z.string().uuid(),
      type: z.string().min(1),
      hours: z.number().nullable().optional(),
      rate: z.number().nullable().optional(),
      rateType: z.enum(['hourly', 'daily']).nullable().optional(),
      unitKey: z.string().optional(),
      amount: z.number().nullable().optional(),
      note: z.string().nullable().optional(),
      markupRate: z.number().min(0).max(1).nullable().optional(),
      sortOrder: z.number().int(),
    })),
  })),
  taxRate: z.coerce.number().min(0).max(1),
  markupRate: z.coerce.number().min(0).max(1),
  roundingPrecision: z.coerce.number().min(0).max(10).nullable().optional(),
});

export type SaveBreakdownInput = z.infer<typeof saveBreakdownInputSchema>;

// Reorder schema
export const reorderInputSchema = z.object({
  items: z.array(z.object({
    id: z.string().uuid(),
    sortOrder: z.number().int(),
  })),
});

// Version schemas
export const breakdownVersionSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  name: z.string().min(1),
  tag: z.string().nullable().optional(),
  snapshot: z.object({
    lineItems: z.array(breakdownLineItemWithSubItemsSchema),
    totalCost: z.number(),
  }),
  totalCost: z.number(),
  createdBy: z.string().uuid(),
  createdAt: isoDateTime(),
  slotKey: breakdownSlotKeySchema,
});

export const breakdownVersionInputSchema = z.object({
  name: z.string().min(1, 'Snapshot name is required'),
  tag: z.string().max(60).nullable().optional(),
});

export const updateBreakdownVersionInputSchema = z.object({
  name: z.string().min(1, 'Snapshot name is required'),
  tag: z.string().max(60).nullable().optional(),
});

// Breakdown document schemas (word-processor model)
export const createBreakdownDocumentInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  tag: z.string().max(60).nullable().optional(),
  // When supplied, the new document is seeded by deep-copying the source's
  // projects/lineItems/subItems. Used by Save As and Duplicate flows.
  sourceDocumentId: z.string().uuid().optional(),
});

export const renameBreakdownDocumentInputSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  tag: z.string().max(60).nullable().optional(),
});

// Payload for replacing a document's full contents (autosave target).
// Rates are PER-DOCUMENT — each draft can have its own — so the client sends
// them in every write. The service uses these (not the job's rates) to
// recompute totalCost server-side.
export const breakdownDocumentContentsSchema = z.object({
  taxRate: z.coerce.number().min(0).max(1),
  markupRate: z.coerce.number().min(0).max(1),
  roundingPrecision: z.coerce.number().min(0).max(10).nullable().optional(),
  projects: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    sortOrder: z.number().int(),
  })),
  lineItems: z.array(z.object({
    id: z.string().uuid(),
    category: z.string(),
    description: z.string().nullable().optional(),
    sortOrder: z.number().int(),
    templateId: z.string().nullable().optional(),
    itemDate: z.string().nullable().optional(),
    projectId: z.string().uuid().nullable().optional(),
    itemized: z.boolean().optional(),
    subItems: z.array(z.object({
      id: z.string().uuid(),
      type: z.string().min(1),
      hours: z.number().nullable().optional(),
      rate: z.number().nullable().optional(),
      rateType: z.enum(['hourly', 'daily']).nullable().optional(),
      unitKey: z.string().optional(),
      amount: z.number().nullable().optional(),
      note: z.string().nullable().optional(),
      markupRate: z.number().min(0).max(1).nullable().optional(),
      sortOrder: z.number().int(),
    })),
  })),
});

export type BreakdownDocumentContents = z.infer<typeof breakdownDocumentContentsSchema>;

// Full job breakdown schema
export const jobBreakdownSchema = z.object({
  lineItems: z.array(breakdownLineItemWithSubItemsSchema),
  totalCost: z.number(),
});

// ============================================================================
// Patch-based saving schemas
// ============================================================================

const lineItemFieldPatchSchema = z.object({
  op: z.literal('updateLineItem'),
  id: z.string().uuid(),
  field: z.enum(['category', 'description', 'sortOrder', 'templateId', 'itemDate']),
  value: z.union([z.string(), z.number(), z.null()]),
});

const subItemFieldPatchSchema = z.object({
  op: z.literal('updateSubItem'),
  id: z.string().uuid(),
  lineItemId: z.string().uuid(),
  field: z.enum(['type', 'hours', 'rate', 'rateType', 'unitKey', 'amount', 'note', 'sortOrder', 'markupRate']),
  value: z.union([z.string(), z.number(), z.null()]),
});

const createLineItemPatchSchema = z.object({
  op: z.literal('createLineItem'),
  id: z.string().uuid(),
  data: z.object({
    category: z.string(),
    description: z.string().optional(),
    sortOrder: z.number().int(),
    templateId: z.string().optional(),
    itemDate: z.string().optional(),
    projectId: z.string().uuid().nullable().optional(),
  }),
});

const rateTypeSchema = z.enum(['hourly', 'daily']);

const createSubItemPatchSchema = z.object({
  op: z.literal('createSubItem'),
  id: z.string().uuid(),
  lineItemId: z.string().uuid(),
  data: z.object({
    type: subItemTypeSchema,
    hours: z.number().optional(),
    rate: z.number().optional(),
    rateType: rateTypeSchema.optional(),
    unitKey: z.string().optional(),
    amount: z.number().optional(),
    note: z.string().optional(),
    markupRate: z.number().min(0).max(1).optional(),
    sortOrder: z.number().int(),
  }),
});

const deleteLineItemPatchSchema = z.object({
  op: z.literal('deleteLineItem'),
  id: z.string().uuid(),
});

const deleteSubItemPatchSchema = z.object({
  op: z.literal('deleteSubItem'),
  id: z.string().uuid(),
  lineItemId: z.string().uuid(),
});

const updateRatesPatchSchema = z.object({
  op: z.literal('updateRates'),
  taxRate: z.number().min(0).max(1).optional(),
  markupRate: z.number().min(0).max(1).optional(),
  roundingPrecision: z.number().min(0).max(10).nullable().optional(),
});

const createProjectPatchSchema = z.object({
  op: z.literal('createProject'),
  id: z.string().uuid(),
  data: z.object({
    name: z.string().min(1),
    sortOrder: z.number().int(),
  }),
});

const updateProjectPatchSchema = z.object({
  op: z.literal('updateProject'),
  id: z.string().uuid(),
  field: z.enum(['name', 'sortOrder']),
  value: z.union([z.string(), z.number()]),
});

const deleteProjectPatchSchema = z.object({
  op: z.literal('deleteProject'),
  id: z.string().uuid(),
});

const moveLineItemPatchSchema = z.object({
  op: z.literal('moveLineItem'),
  id: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  sortOrder: z.number().int(),
});

export const breakdownPatchSchema = z.discriminatedUnion('op', [
  lineItemFieldPatchSchema,
  subItemFieldPatchSchema,
  createLineItemPatchSchema,
  createSubItemPatchSchema,
  deleteLineItemPatchSchema,
  deleteSubItemPatchSchema,
  updateRatesPatchSchema,
  createProjectPatchSchema,
  updateProjectPatchSchema,
  deleteProjectPatchSchema,
  moveLineItemPatchSchema,
]);

export const patchBreakdownInputSchema = z.object({
  patches: z.array(breakdownPatchSchema),
});

// ============================================================================
// Common Template Items schemas
// ============================================================================

export const commonTemplateItemInputSchema = z.object({
  id: z.string().uuid().optional(),
  category: z.string().min(1),
  description: z.string().optional(),
  isReusable: z.boolean().optional(),
  isDisclaimer: z.boolean().optional(),
  sortOrder: z.number().int(),
});

export const commonTemplateItemsBulkInputSchema = z.object({
  items: z.array(commonTemplateItemInputSchema),
});
